import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";
import BcCouponCard from "./BcCouponCard";
import FilterModal from "./FilterModal";
import SortModal from "./SortModal";
import Footer from "../footer/Footer";
import ProfileHeader from "../header/ProfileHeader";
import ChevronDownIcon from "../icons/heroicons/solid/ChevronDown";
import CouponCategoryAllIcon from "../../assets/images/coupon-category-all-icon-2025.jpg";
import ListLayoutIcon from "../../assets/images/list-layout-icon.png";
import GridLayoutIcon from "../../assets/images/grid-layout-icon.png";
import FilterIcon from "../../assets/images/filter-icon.png";
import { isSameCouponCriteria } from "../helpers/bcCouponHelper";
import { ItemHelper } from "../helpers/item-helper";
import LoadingBar from "../LoadingBar";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom"; // ⬅️ เพิ่มบรรทัดนี้

const DEFAULT_PAGE_SIZE = 10;
const SORTS = [
  { name: "sort_by_default", value: null },
  { name: "sort_by_name_asc", value: { sortBy: "name", sortType: "asc" } },
  { name: "sort_by_name_desc", value: { sortBy: "name", sortType: "desc" } },
  {
    name: "sort_by_redeem_point_asc",
    value: { sortBy: "redeemPoint", sortType: "asc" },
  },
  {
    name: "sort_by_update_date",
    value: { sortBy: "updateDate", sortType: "desc" },
  },
];
const REDEEM_POINT_RANGE = { min: 0, max: 999 };
const LAYOUT_MODES = ["horizontal", "vertical"];
const defaultCategory = {
  name: "All",
  criteria: { couponType: "", categoryCode: "", hotDeals: false },
  icon: CouponCategoryAllIcon,
};

const clientsThis = async (headers) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/clients/this`,
      { headers }
    );
    return res.data?.data?.variables.bc_coupons_page_id;
  } catch {
    return "";
  }
};

const getPage = async (headers, pageID) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/pages/${pageID}`,
      { headers }
    );
    return res.data?.data?.sections[0].items || [];
  } catch {
    return [];
  }
};

const getBcCoupons = async (request, headers) => {
  try {
    const params = new URLSearchParams();
    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/clm-coupons?${params.toString()}`,
      { headers }
    );
    return res.data?.data || { items: [], total: 0 };
  } catch (error) {
    console.error("getBcCoupons error", error);
    return { items: [], total: 0 };
  }
};

const convertAndFilterBcCouponActiveCategories = async (
  headers,
  items,
  language
) => {
  try {
    const cats = items.map((item) => {
      const { image, code } = item;
      const name = ItemHelper.getName(item, language);
      const criteria = ItemHelper.convertCodeToCouponCriteria(code);
      return { name, criteria, icon: image };
    });

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/clm-coupons/filter-categories`,
      { categories: cats.map((cat) => ({ criteria: cat.criteria })) },
      { headers }
    );

    if (response?.data?.statusCode === 200) {
      const activeCriteriaList = response.data.data?.items || [];
      return cats.filter((cat) =>
        activeCriteriaList.some((activeCat) =>
          isSameCouponCriteria(cat.criteria, activeCat.criteria)
        )
      );
    } else {
      console.warn("API status not 200:", response?.data?.message);
      return [];
    }
  } catch (error) {
    console.error("convertAndFilterBcCouponActiveCategories error:", error);
    return [];
  }
};

export default function BcCoupons() {
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
      "x-user-id": userId,
      "Accept-Language": i18n.language.toUpperCase(),
    }),
    [authToken, userId, i18n.language]
  );

  const [searchingParams, setSearchingParams] = useState({
    pageNo: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    couponType: "",
    categoryCode: "",
    sort: SORTS[0],
    redeemPoint: undefined,
  });

  const [showSortModal, setShowSortModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const [bcCoupons, setBcCoupons] = useState([]);
  const [totalBcCoupons, setTotalBcCoupons] = useState(0);
  const [bcCouponCategory, setBcCouponCategory] = useState([]);
  const [bcCouponActiveCategories, setBcCouponActiveCategories] = useState([]);
  const [bcCouponLayout, setBcCouponLayout] = useState(0);
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";
  const location = useLocation(); // ⬅️ เพิ่ม
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialCategoryFromUrl = queryParams.get("category");

  //หา category จาก query
  const getCategoryFromQuery = (categories, queryValue) => {
    if (!queryValue || !categories || !categories.length) return null;

    if (queryValue === "hot_deals") {
      return categories.find((cat) => cat.criteria?.hotDeals);
    }

    return categories.find(
      (cat) =>
        cat.criteria?.categoryCode?.toLowerCase() === queryValue.toLowerCase()
    );
  };

  const categories = bcCouponActiveCategories.length
    ? bcCouponActiveCategories
    : [defaultCategory];
  const activeCategory = bcCouponCategory?.name || categories[0]?.name;
  const isListLayout = bcCouponLayout === 0;
  const sortIndex = SORTS.findIndex(
    (s) => s.name === searchingParams.sort?.name
  );
  const radioLabels = SORTS.map((s) => s.name);

  const fetchData = async (pageNo, overrideParams = null) => {
    setLoadingCoupon(true);
    try {
      const sourceParams = overrideParams || searchingParams;
      const request = {
        pageNo,
        pageSize: sourceParams.pageSize,
        couponType: sourceParams.couponType,
        categoryCode: sourceParams.categoryCode,
        hotDeals: sourceParams.hotDeals,
        sortBy: sourceParams.sort?.value?.sortBy,
        sortDirection: sourceParams.sort?.value?.sortType,
        minRedeemPoint: sourceParams.redeemPoint?.min,
        maxRedeemPoint: sourceParams.redeemPoint?.max,
      };

      const result = await getBcCoupons(request, headers);
      setBcCoupons((prev) =>
        pageNo === 1 ? result.items : [...prev, ...result.items]
      );
      setTotalBcCoupons(result.total);
    } catch (err) {
      console.error("fetchData error:", err);
      setBcCoupons([]);
    } finally {
      setLoadingCoupon(false);
    }
  };

  const fetchCoupons = async (reset = false) => {
    let pageNo = searchingParams.pageNo;
    if (reset) {
      pageNo = 1;
      setBcCoupons([]);
    } else if (bcCoupons.length >= totalBcCoupons) {
      return;
    } else {
      pageNo += 1;
    }

    setSearchingParams((prev) => ({ ...prev, pageNo }));
    await fetchData(pageNo);
  };

  useEffect(() => {
    const init = async () => {
      const pageId = await clientsThis(headers);
      const menuItems = await getPage(headers, pageId);
      const activeCategories = await convertAndFilterBcCouponActiveCategories(
        headers,
        menuItems,
        i18n.language
      );
      setBcCouponActiveCategories(activeCategories);

      const matchedCategory = getCategoryFromQuery(
        activeCategories,
        initialCategoryFromUrl
      );
      if (matchedCategory) {
        setBcCouponCategory(matchedCategory);
        const newParams = {
          ...searchingParams,
          ...matchedCategory.criteria,
          pageNo: 1,
          pageSize: matchedCategory.pageSize || DEFAULT_PAGE_SIZE,
        };
        setSearchingParams(newParams);
        await fetchData(1, newParams);
      } else {
        fetchCoupons(true);
      }
    };

    if (authToken && userId) {
      init();

      // ✅ GA4 Event: All Coupon View
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "all_coupon_view",
        user_id: userId,
        category_code: initialCategoryFromUrl || "all",
      });
    }
  }, [authToken, userId, headers, initialCategoryFromUrl]);

  const onCategoryClick = useCallback(
    (category) => {
      // ✅ GA4 Event: Change Category
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "all_coupon_view",
        user_id: userId,
        category: category.name,
        category_code: category.criteria?.categoryCode || "",
      });
      setBcCoupons([]);
      setBcCouponCategory(category);
      const newParams = {
        ...searchingParams,
        ...category.criteria,
        pageNo: 1,
        pageSize: category.pageSize || DEFAULT_PAGE_SIZE,
      };
      setSearchingParams(newParams);
      fetchData(1, newParams);
    },
    [searchingParams]
  );

  const toggleLayout = () => setBcCouponLayout(1 - bcCouponLayout);

  const onSortModalOk = (index) => {
    setShowSortModal(false);
    const newSort = SORTS[index];
    if (newSort && newSort !== searchingParams.sort) {
      const newParams = { ...searchingParams, sort: newSort, pageNo: 1 };
      setSearchingParams(newParams);
      fetchData(1, newParams);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 1 && !loadingCoupon) {
      fetchCoupons(false);
    }
  };

  return (
    <div className="h-screen relative bg-secondary-background-gray">
      <div
        className="w-full h-full overflow-y-auto"
        onScroll={handleScroll}
        ref={scrollRef}
      >
        <ProfileHeader className="bg-primary" />
        <div
          className={`w-full flex px-5 pb-3 font-normal overflow-x-auto overflow-y-hidden whitespace-nowrap ${
            seniorLayout ? "pt-2 gap-x-2.5 text-sm" : "pt-4 gap-x-3 text-xs"
          }`}
        >
          {categories.map((category, index) => (
            <div
              key={`category-${index}`}
              onClick={() => onCategoryClick(category)}
              className={`h-full flex-none flex flex-col items-center text-center cursor-pointer ${
                seniorLayout ? "w-[5.75rem]" : "w-20"
              }`}
            >
              <div
                className={`w-full rounded-full overflow-hidden ${
                  activeCategory === category.name
                    ? "border-4 border-primary"
                    : ""
                }`}
              >
                <img
                  src={category.icon}
                  alt={category.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div
                className="max-w-[80px] mt-1 text-sm leading-tight break-words text-ellipsis text-wrap text-balance line-clamp-2"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {category.name}
              </div>
            </div>
          ))}
        </div>
        <div className="w-full px-5 xs:px-2 flex flex-col gap-y-3 my-5 text-left">
          <div
            className={`font-semibold ${seniorLayout ? "text-2xl" : "text-22"}`}
          >
            {activeCategory}
          </div>
          <div className="w-full flex xs:flex-wrap gap-3 sm:gap-x-1">
            <div
              className="w-56 h-9 flex-shrink-0 flex items-center bg-overlay-5 rounded-full px-2 gap-x-1 cursor-pointer"
              onClick={() => setShowSortModal(true)}
            >
              <ChevronDownIcon className="w-5 h-5" />
              <span className="text-base font-medium ml-2">
                {t("sort")}: {t(searchingParams.sort?.name)}
              </span>
            </div>
            <div className="ml-auto flex gap-2">
              <button
                className="bg-overlay-5 rounded-full p-2"
                onClick={() => setShowFilterModal(true)}
              >
                <img src={FilterIcon} className="w-6 h-6" />
              </button>
              <button
                className="bg-overlay-5 rounded-full p-2"
                onClick={toggleLayout}
              >
                <img
                  src={isListLayout ? ListLayoutIcon : GridLayoutIcon}
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>
        </div>

        <div
          className={`px-5 grid gap-4 ${
            isListLayout ? "grid-cols-1" : "grid-cols-2"
          }`}
        >
          {bcCoupons.map((item, index) => (
            <BcCouponCard
              key={`bc-coupon-${index}`}
              value={item}
              mode={LAYOUT_MODES[bcCouponLayout]}
              onClick={(id) => navigate(`/bc-coupons/${id}`)}
            />
          ))}
        </div>
      </div>

      <LoadingBar loading={loadingCoupon} label="Loading..." />
      <Footer className="fixed bottom-0" />

      <SortModal
        show={showSortModal}
        value={sortIndex}
        radioLabels={radioLabels}
        seniorLayout={seniorLayout}
        onClose={() => setShowSortModal(false)}
        onOk={onSortModalOk}
      />

      <FilterModal
        show={showFilterModal}
        min={REDEEM_POINT_RANGE.min}
        max={REDEEM_POINT_RANGE.max}
        seniorLayout={seniorLayout}
        onClose={() => setShowFilterModal(false)}
        onClear={() => {
          const newParams = {
            ...searchingParams,
            redeemPoint: undefined,
            pageNo: 1,
          };
          setSearchingParams(newParams);
          fetchData(1, newParams);
          setShowFilterModal(false);
        }}
        onSearch={(range) => {
          const newParams = {
            ...searchingParams,
            redeemPoint: { min: range[0], max: range[1] },
            pageNo: 1,
          };
          setSearchingParams(newParams);
          fetchData(1, newParams);
          setShowFilterModal(false);
        }}
      />
    </div>
  );
}
